// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites layer in the headset and phone AR: the orbit shell
 * (docs/AYNI_XR_SATELLITES_PLAN.md, the flagship). Every shown object flies
 * at its own height around the primary globe, a dot with a fading trail,
 * so low orbits hug the globe and the geostationary belt rings it about
 * 6.6 globe radii out, a few metres across the room at true scale. The
 * "compact" height squeezes the shell (GEO at 1.6 radii) so it stays
 * within reach. The selected object shows its whole orbit as a ring
 * through it, and the space stations carry their names.
 *
 * Everything is a child of the globe, so it turns, tilts and grows with
 * it. Positions come from the page's SatelliteEngine as Earth-fixed km:
 * globe-local x = X, y = Z, z = −Y (latLonToGlobeLocal's frame).
 *
 * One geometry layout, (K + 1) × n vertices (the K trail slots, then the
 * current positions), shared by the trail lines and the dots; the trails'
 * "segment ends here" flag is a `flat` varying, taken from a line's last
 * vertex, as in the browser layer (satelliteMapLayer.ts).
 */
import type * as THREE from 'three'
import { mod, TRAIL_SLOTS, TRAIL_STEP_S } from './satellites/satelliteEngine'
import { isDebris, type AltitudeScale, type SatelliteLayer } from './satellites/satelliteLayer'
import { GLOBE_RADIUS_KM } from './satellites/satelliteOrbit'

const K = TRAIL_SLOTS
const GEO_ALT_KM = 35786

/** Distance from the centre in globe radii for a height above the surface. */
export function shellRadius(altKm: number, scale: AltitudeScale): number {
  if (scale === 'true') return 1 + altKm / GLOBE_RADIUS_KM
  // Compact: LEO ≈ 1.06, GNSS ≈ 1.45, GEO = 1.6.
  return 1 + 0.6 * Math.sqrt(Math.max(0, altKm) / GEO_ALT_KM)
}

/** Earth-fixed km → globe-local metres, written at out[o..o+2]. */
export function ecfToLocal(
  x: number,
  y: number,
  z: number,
  R: number,
  scale: AltitudeScale,
  out: Float32Array,
  o: number,
): void {
  if (x !== x) {
    out[o] = out[o + 1] = out[o + 2] = NaN
    return
  }
  const r = Math.hypot(x, y, z)
  const k = (R * shellRadius(r - GLOBE_RADIUS_KM, scale)) / r
  out[o] = x * k
  out[o + 1] = z * k
  out[o + 2] = -y * k
}

const POINT_VERT = /* glsl */ `
in vec3 aColour;
in float aKind;
uniform float uSize;
uniform float uViewH;
uniform float uMinPx;
uniform float uMaxPx;
out vec3 vColour;
out float vKind;
void main() {
  vKind = aKind;
  vColour = (aKind > 2.5 && aKind < 3.5) ? vec3(1.0) : aColour;
  if (aKind < 0.5 || any(isnan(position))) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  // A source of the data (5) gets room for its halo.
  float s = aKind > 5.5 ? 2.4 : aKind > 4.5 ? 4.8 : aKind > 3.5 ? 0.6 : aKind > 2.5 ? 2.4 : aKind > 1.5 ? 2.0 : 1.0;
  float px = uSize * projectionMatrix[1][1] * uViewH * 0.5 / max(0.05, -mv.z);
  gl_PointSize = clamp(px, uMinPx, uMaxPx) * s;
}
`

const POINT_FRAG = /* glsl */ `
in vec3 vColour;
in float vKind;
uniform float uTime;
out highp vec4 satColour;
void main() {
  vec2 c = gl_PointCoord * 2.0 - 1.0;
  float r = length(c);
  if (r > 1.0) discard;
  if (vKind > 5.5) {
    // Feeds a dataset of the catalog (owner, 2026-10-24): its own dot inside a steady fuchsia ring.
    float core = 1.0 - smoothstep(0.34, 0.44, r);
    float ring = smoothstep(0.62, 0.70, r) * (1.0 - smoothstep(0.86, 0.96, r));
    vec3 col = mix(vColour, vec3(0.03, 0.04, 0.06), smoothstep(0.22, 0.34, r)) * core + vec3(1.0, 0.0, 0.8) * ring;
    satColour = vec4(col, max(core, ring * 0.95));
    return;
  }
  if (vKind > 4.5) {
    // A source of the data: a bright core and a halo that swells and fades (1.6 s).
    float pulse = fract(uTime * 0.625);
    float ring = 0.42 + 0.56 * pulse;
    float halo = (1.0 - smoothstep(0.0, 0.09, abs(r - ring))) * (1.0 - pulse);
    float core = 1.0 - smoothstep(0.30, 0.42, r);
    vec3 col = mix(vec3(1.0, 0.0, 0.8), vec3(1.0, 0.7, 1.0), 0.45 * (1.0 - pulse));
    col = mix(col, vec3(0.03, 0.04, 0.06), smoothstep(0.22, 0.30, r) * core * 0.6);
    satColour = vec4(col, max(core, halo * 0.9));
    return;
  }
  float a = 1.0 - smoothstep(0.84, 1.0, r);
  vec3 col = mix(vColour, vec3(0.03, 0.04, 0.06), smoothstep(0.55, 0.75, r) * 0.7);
  if (vKind > 1.5 && vKind < 3.5) {
    float ring = smoothstep(0.30, 0.38, r) * (1.0 - smoothstep(0.50, 0.58, r));
    col = mix(col, vec3(0.03, 0.04, 0.06), ring);
  }
  satColour = vec4(col, a);
}
`

const TRAIL_VERT = /* glsl */ `
in vec3 aColour;
in float aKind;
in float aSlot;
in float aOk;
uniform float uAges[${K + 1}];
uniform float uSpan;
out vec3 vColour;
out float vAlpha;
flat out float vOk;
void main() {
  vColour = aColour;
  vOk = aOk;
  if (aKind < 0.5 || any(isnan(position))) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vOk = 0.0;
    vAlpha = 0.0;
    return;
  }
  float age = uAges[int(aSlot + 0.5)];
  float a = clamp(1.0 - age / uSpan, 0.0, 1.0);
  vAlpha = a * a * (aKind > 3.5 && aKind < 4.5 ? 0.4 : 0.8);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const TRAIL_FRAG = /* glsl */ `
in vec3 vColour;
in float vAlpha;
flat in float vOk;
out highp vec4 satColour;
void main() {
  if (vOk < 0.5) discard;
  satColour = vec4(vColour, vAlpha);
}
`

/** Earth-fixed km → the group's local coordinates, written at out[o..o+2] (NaN hides it). */
export type SatelliteProjector = (x: number, y: number, z: number, out: Float32Array, o: number) => void

export interface VrSatellitesOptions {
  /** Default: the orbit shell around the globe, at the layer's height setting. */
  project?: SatelliteProjector
  /** Changes whenever `project` would place things differently; everything is placed again. */
  projectKey?: () => string
  /** A dot's size in metres where it stands (default 7 mm, the shell's). */
  pointSize?: number
  /** The smallest a dot is drawn, px (default 2.5). */
  minPx?: number
  /** The faint geostationary ring (the shell's guide). Default true. */
  geoRing?: boolean
  /** The selected object's path: its orbit ellipse (default) or its ground track. */
  path?: 'ellipse' | 'track'
  /** What hides objects behind it when picking; default the globe, null for none. */
  occluder?: { centre(out: THREE.Vector3): THREE.Vector3; radius(): number } | null
  /** Drawn only while this says so (default always, with the layer on). */
  visible?: () => boolean
}

export interface VrSatellitesHandle {
  readonly group: THREE.Group
  /** Per XR frame: positions, trails, the selection's orbit and labels. */
  update(camera: THREE.Camera, viewportHeightPx: number): void
  /**
   * The shown object nearest a ray (within a few degrees, not hidden by
   * the globe), or null. Phones get a wider reach than controllers.
   */
  pick(origin: THREE.Vector3, direction: THREE.Vector3, handheld: boolean): number | null
  dispose(): void
}

export function createVrSatellites(
  THREE_: typeof THREE,
  parent: THREE.Object3D,
  R: number,
  layer: SatelliteLayer,
  opts: VrSatellitesOptions = {},
): VrSatellitesHandle {
  const engine = layer.engine
  const group = new THREE_.Group()
  group.name = 'satellites'
  parent.add(group)
  const project: SatelliteProjector =
    opts.project ?? ((x, y, z, out, o) => ecfToLocal(x, y, z, R, layer.settings.altitude, out, o))
  const projectKey = opts.projectKey ?? (() => layer.settings.altitude)
  const occluder =
    opts.occluder === undefined
      ? {
          centre: (out: THREE.Vector3) => parent.getWorldPosition(out),
          radius: () => R * (parent.getWorldScale(scratchScale).x || 1),
        }
      : opts.occluder
  const scratchScale = new THREE_.Vector3()

  const pointMat = new THREE_.ShaderMaterial({
    glslVersion: THREE_.GLSL3,
    vertexShader: POINT_VERT,
    fragmentShader: POINT_FRAG,
    uniforms: {
      // A dot about 7 mm across at true scale, never under 2.5 px or over 14.
      uSize: { value: opts.pointSize ?? 0.007 },
      uViewH: { value: 1000 },
      uMinPx: { value: opts.minPx ?? 2.5 },
      uMaxPx: { value: 14 },
      // The sources' pulse (DSA 1.10 platforms): seconds, wrapped.
      uTime: { value: 0 },
    },
    transparent: true,
    depthWrite: false,
  })
  const trailMat = new THREE_.ShaderMaterial({
    glslVersion: THREE_.GLSL3,
    vertexShader: TRAIL_VERT,
    fragmentShader: TRAIL_FRAG,
    uniforms: { uAges: { value: new Array(K + 1).fill(1e9) }, uSpan: { value: K * TRAIL_STEP_S } },
    transparent: true,
    depthWrite: false,
  })
  const orbitMat = new THREE_.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false })
  const markMat = new THREE_.LineBasicMaterial({ color: 0xffb020, transparent: true, opacity: 0.95, depthWrite: false })
  const fovMat = new THREE_.LineBasicMaterial({ color: 0xff00cc, transparent: true, opacity: 0.6, depthWrite: false })
  const geoRingMat = new THREE_.LineBasicMaterial({ color: 0xffb020, transparent: true, opacity: 0.22, depthWrite: false })

  let n = 0
  let layoutSeen = -1
  let styleSeen = -1
  let keySeen: string | null = null
  let pos: THREE.BufferAttribute | null = null
  let colour: THREE.BufferAttribute | null = null
  let kind: THREE.BufferAttribute | null = null
  let okAttr: THREE.BufferAttribute | null = null
  let pointsGeo: THREE.BufferGeometry | null = null
  let trailGeo: THREE.BufferGeometry | null = null
  let headGeo: THREE.BufferGeometry | null = null
  let points: THREE.Points | null = null
  let trails: THREE.LineSegments | null = null
  let heads: THREE.LineSegments | null = null
  let headNewest = -2
  const reader = {}

  let orbitLine: THREE.Line | null = null
  let markLines: THREE.LineSegments | null = null
  let fovLines: THREE.LineSegments | null = null
  let fovSeen = -1
  let fovProjSeen = ''
  let orbitSeen: unknown = null

  const geoRing = new THREE_.LineLoop(new THREE_.BufferGeometry(), geoRingMat)
  geoRing.frustumCulled = false
  geoRing.renderOrder = 6
  group.add(geoRing)
  let geoRingScale: AltitudeScale | null = null

  const labels = new Map<number, THREE.Sprite>()

  const big = new THREE_.Sphere(new THREE_.Vector3(), 1e6)

  function disposeLayout(): void {
    for (const o of [points, trails, heads]) if (o) group.remove(o)
    pointsGeo?.dispose()
    trailGeo?.dispose()
    headGeo?.dispose()
    points = trails = heads = null
  }

  function rebuildLayout(): void {
    disposeLayout()
    n = engine.count
    layoutSeen = engine.layoutVersion
    styleSeen = -1
    headNewest = -2
    keySeen = null
    engine.releaseReader(reader)
    const total = (K + 1) * n
    pos = new THREE_.BufferAttribute(new Float32Array(total * 3).fill(NaN), 3)
    pos.setUsage(THREE_.DynamicDrawUsage)
    colour = new THREE_.BufferAttribute(new Float32Array(total * 3), 3)
    kind = new THREE_.BufferAttribute(new Float32Array(total), 1)
    okAttr = new THREE_.BufferAttribute(new Float32Array(total), 1)
    okAttr.setUsage(THREE_.DynamicDrawUsage)
    const slot = new Float32Array(total)
    for (let s = 0; s <= K; s++) slot.fill(s, s * n, (s + 1) * n)
    const slotAttr = new THREE_.BufferAttribute(slot, 1)

    pointsGeo = new THREE_.BufferGeometry()
    pointsGeo.setAttribute('position', pos)
    pointsGeo.setAttribute('aColour', colour)
    pointsGeo.setAttribute('aKind', kind)
    pointsGeo.setDrawRange(K * n, n)
    pointsGeo.boundingSphere = big

    const shared = (g: THREE.BufferGeometry) => {
      g.setAttribute('position', pos!)
      g.setAttribute('aColour', colour!)
      g.setAttribute('aKind', kind!)
      g.setAttribute('aSlot', slotAttr)
      g.setAttribute('aOk', okAttr!)
      g.boundingSphere = big
    }
    trailGeo = new THREE_.BufferGeometry()
    shared(trailGeo)
    const idx = new Uint32Array(K * n * 2)
    let o = 0
    for (let s = 0; s < K; s++) {
      const p = mod(s - 1, K)
      for (let i = 0; i < n; i++) {
        idx[o++] = p * n + i
        idx[o++] = s * n + i
      }
    }
    trailGeo.setIndex(new THREE_.BufferAttribute(idx, 1))
    headGeo = new THREE_.BufferGeometry()
    shared(headGeo)
    headGeo.setIndex(new THREE_.BufferAttribute(new Uint32Array(n * 2), 1))

    trails = new THREE_.LineSegments(trailGeo, trailMat)
    heads = new THREE_.LineSegments(headGeo, trailMat)
    points = new THREE_.Points(pointsGeo, pointMat)
    for (const obj of [trails, heads, points]) {
      obj.frustumCulled = false
      group.add(obj)
    }
    trails.renderOrder = 7
    heads.renderOrder = 7
    points.renderOrder = 8
  }

  function uploadStyle(): void {
    if (!colour || !kind) return
    styleSeen = engine.styleVersion
    const c = colour.array as Float32Array
    const k = kind.array as Float32Array
    const sel = layer.selected
    for (let i = 0; i < n; i++) {
      const info = engine.infos[i]
      const kd = !engine.mask[i] ? 0 : i === sel ? 3 : layer.isSource(i) ? 5 : layer.isCatalogSource(i) ? 6 : info.station ? 2 : isDebris(info) ? 4 : 1
      const r = engine.colour[i * 3]
      const g = engine.colour[i * 3 + 1]
      const b = engine.colour[i * 3 + 2]
      for (let s = 0; s <= K; s++) {
        const v = s * n + i
        k[v] = kd
        c[v * 3] = r
        c[v * 3 + 1] = g
        c[v * 3 + 2] = b
      }
    }
    colour.needsUpdate = true
    kind.needsUpdate = true
  }

  /**
   * AYNI (Quest review D9, 2026-11-03): only the slots written this frame go
   * to the GPU. Marking the whole buffer re-uploaded every slot (13 × n
   * positions, ~2 MB a frame at ~10,000 objects) to change the newest one.
   */
  function markSlot(s: number): void {
    pos!.addUpdateRange(s * n * 3, n * 3)
    okAttr!.addUpdateRange(s * n, n)
  }

  function slotFlags(s: number, prevSlot: number, prevOk: boolean): void {
    const ok = okAttr!.array as Float32Array
    const p = pos!.array as Float32Array
    for (let i = 0; i < n; i++) {
      const v = s * n + i
      const pv = prevSlot * n + i
      ok[v] = prevOk && p[v * 3] === p[v * 3] && p[pv * 3] === p[pv * 3] ? 1 : 0
    }
  }

  function uploadTrail(key: string): void {
    const all = key !== keySeen
    keySeen = key
    const dirty = engine.takeDirtySlots(reader)
    const slots = all ? Array.from({ length: K }, (_, s) => s) : dirty
    if (!slots.length) return
    const p = pos!.array as Float32Array
    const tr = engine.trail
    for (const s of slots) {
      for (let i = 0; i < n; i++) {
        const src = (s * n + i) * 3
        project(tr[src], tr[src + 1], tr[src + 2], p, src)
      }
    }
    for (const s of slots) {
      slotFlags(s, mod(s - 1, K), engine.slotHasPrevious(s))
      markSlot(s)
    }
    pos!.needsUpdate = true
    okAttr!.needsUpdate = true
  }

  function uploadHead(): void {
    const p = pos!.array as Float32Array
    const cur = engine.current
    const base = K * n * 3
    for (let i = 0; i < n; i++) {
      const src = i * 3
      project(cur[src], cur[src + 1], cur[src + 2], p, base + src)
    }
    const newest = engine.newestSlot()
    slotFlags(K, Math.max(0, newest), newest >= 0 && layer.settings.trails)
    markSlot(K)
    pos!.needsUpdate = true
    okAttr!.needsUpdate = true
    if (newest !== headNewest && headGeo) {
      headNewest = newest
      const idx = headGeo.index!.array as Uint32Array
      for (let i = 0; i < n; i++) {
        idx[i * 2] = Math.max(0, newest) * n + i
        idx[i * 2 + 1] = K * n + i
      }
      headGeo.index!.needsUpdate = true
    }
  }

  function updateOrbit(projKey: string): void {
    const orbit = layer.selectedOrbit
    const key = orbit ? `${orbit.id}:${projKey}` : null
    if (key === orbitSeen) return
    orbitSeen = key
    if (orbitLine) {
      group.remove(orbitLine)
      orbitLine.geometry.dispose()
      orbitLine = null
    }
    if (markLines) {
      group.remove(markLines)
      markLines.geometry.dispose()
      markLines = null
    }
    if (!orbit) return
    const track = opts.path === 'track'
    const src = track ? orbit.track : orbit.ellipse
    const out = new Float32Array(src.length)
    for (let o = 0; o < src.length; o += 3) project(src[o], src[o + 1], src[o + 2], out, o)
    const g = new THREE_.BufferGeometry()
    g.setAttribute('position', new THREE_.BufferAttribute(out, 3))
    orbitLine = track ? new THREE_.Line(g, orbitMat) : new THREE_.LineLoop(g, orbitMat)
    orbitLine.frustumCulled = false
    orbitLine.renderOrder = 9
    group.add(orbitLine)
    // Where an object came down: a ring on the ground and a pin up from it.
    if (orbit.marks?.length) {
      const m = new Float32Array(orbit.marks.length)
      for (let o = 0; o < m.length; o += 3) project(orbit.marks[o], orbit.marks[o + 1], orbit.marks[o + 2], m, o)
      const mg = new THREE_.BufferGeometry()
      mg.setAttribute('position', new THREE_.BufferAttribute(m, 3))
      markLines = new THREE_.LineSegments(mg, markMat)
      markLines.frustumCulled = false
      markLines.renderOrder = 10
      group.add(markLines)
    }
  }

  /** What the sources and the selected object see, on the globe's surface. */
  function updateFootprints(projKey: string): void {
    if (layer.footprintsVersion === fovSeen && fovProjSeen === projKey) return
    fovSeen = layer.footprintsVersion
    fovProjSeen = projKey
    const src = layer.footprints
    if (!src.length) {
      if (fovLines) {
        group.remove(fovLines)
        fovLines.geometry.dispose()
        fovLines = null
      }
      return
    }
    const out = new Float32Array(src.length)
    for (let o = 0; o < src.length; o += 3) project(src[o], src[o + 1], src[o + 2], out, o)
    if (!fovLines || (fovLines.geometry.getAttribute('position') as THREE.BufferAttribute).count !== out.length / 3) {
      if (fovLines) {
        group.remove(fovLines)
        fovLines.geometry.dispose()
      }
      const g = new THREE_.BufferGeometry()
      g.setAttribute('position', new THREE_.BufferAttribute(out, 3))
      fovLines = new THREE_.LineSegments(g, fovMat)
      fovLines.frustumCulled = false
      fovLines.renderOrder = 8
      group.add(fovLines)
    } else {
      const attr = fovLines.geometry.getAttribute('position') as THREE.BufferAttribute
      ;(attr.array as Float32Array).set(out)
      attr.needsUpdate = true
    }
  }

  function updateGeoRing(scale: AltitudeScale): void {
    const shown =
      opts.geoRing !== false &&
      layer.settings.colourBy === 'orbit' &&
      layer.legend().some((e) => e.key === 'geo' && !e.hidden)
    geoRing.visible = shown
    if (!shown || geoRingScale === scale) return
    geoRingScale = scale
    const r = R * shellRadius(GEO_ALT_KM, scale)
    const pts: number[] = []
    for (let a = 0; a < 256; a++) {
      const t = (a / 256) * Math.PI * 2
      pts.push(r * Math.cos(t), 0, -r * Math.sin(t))
    }
    geoRing.geometry.dispose()
    geoRing.geometry = new THREE_.BufferGeometry()
    geoRing.geometry.setAttribute('position', new THREE_.BufferAttribute(new Float32Array(pts), 3))
  }

  function labelSprite(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 64
    const g = canvas.getContext('2d')!
    g.font = '600 34px system-ui, sans-serif'
    g.textBaseline = 'middle'
    g.lineWidth = 6
    g.strokeStyle = 'rgba(5, 8, 14, 0.9)'
    g.strokeText(text, 8, 32)
    g.fillStyle = '#ffffff'
    g.fillText(text, 8, 32)
    const tex = new THREE_.CanvasTexture(canvas)
    tex.colorSpace = THREE_.SRGBColorSpace
    const mat = new THREE_.SpriteMaterial({ map: tex, transparent: true, depthWrite: false })
    const sprite = new THREE_.Sprite(mat)
    sprite.center.set(0, 0.5)
    sprite.renderOrder = 10
    return sprite
  }

  /** The stations (and the selected object) carry their names, a fixed angular size. */
  function updateLabels(camera: THREE.Camera): void {
    const want = new Set<number>()
    for (let i = 0; i < n; i++) if (engine.mask[i] && (engine.infos[i].station || layer.isSource(i))) want.add(i)
    if (layer.selected !== null && engine.mask[layer.selected]) want.add(layer.selected)
    for (const [i, s] of labels) {
      if (want.has(i)) continue
      group.remove(s)
      s.material.map?.dispose()
      s.material.dispose()
      labels.delete(i)
    }
    const p = pos!.array as Float32Array
    const cam = scratchA
    camera.getWorldPosition(cam)
    for (const i of want) {
      let s = labels.get(i)
      if (!s) {
        s = labelSprite(shortName(engine.infos[i].name))
        labels.set(i, s)
        group.add(s)
      }
      const o = (K * n + i) * 3
      if (p[o] !== p[o]) {
        s.visible = false
        continue
      }
      s.visible = true
      s.position.set(p[o], p[o + 1], p[o + 2])
      // About 1.6° tall wherever the viewer stands, in the globe's own scale.
      s.getWorldPosition(scratchB)
      const d = cam.distanceTo(scratchB)
      const worldScale = group.getWorldScale(scratchC).x || 1
      const h = (d * 0.028) / worldScale
      s.scale.set(h * 4, h, 1)
    }
  }

  const scratchA = new THREE_.Vector3()
  const scratchB = new THREE_.Vector3()
  const scratchC = new THREE_.Vector3()
  const scratchD = new THREE_.Vector3()

  return {
    group,

    update(camera, viewportHeightPx) {
      // Not around another body (owner, 2026-10-23): satellites orbit the Earth.
      group.visible = layer.shown && engine.count > 0 && (opts.visible?.() ?? true)
      if (!group.visible) return
      if (engine.layoutVersion !== layoutSeen) rebuildLayout()
      if (!pos) return
      if (engine.styleVersion !== styleSeen) uploadStyle()
      const scale = layer.settings.altitude
      const key = projectKey()
      uploadTrail(key)
      uploadHead()
      if (trails) trails.visible = layer.settings.trails
      if (heads) heads.visible = layer.settings.trails
      const ages = trailMat.uniforms.uAges.value as number[]
      for (let s = 0; s < K; s++) {
        const t = engine.slotTime(s)
        ages[s] = t === t ? Math.max(0, (engine.time - t) / 1000) : 1e9
      }
      ages[K] = 0
      pointMat.uniforms.uViewH.value = viewportHeightPx
      pointMat.uniforms.uTime.value = (performance.now() % 100_000) / 1000
      updateOrbit(key)
      updateGeoRing(scale)
      updateLabels(camera)
    },

    pick(origin, direction, handheld) {
      if (!layer.shown || !pos || !n || !group.visible) return null
      group.updateWorldMatrix(true, false)
      const m = group.matrixWorld
      const p = pos.array as Float32Array
      const dir = scratchA.copy(direction).normalize()
      // The globe hides what is behind it.
      let globeHit = Infinity
      if (occluder) {
        const centre = occluder.centre(scratchB)
        const radius = occluder.radius()
        const oc = scratchC.copy(origin).sub(centre)
        const bq = oc.dot(dir)
        const disc = bq * bq - (oc.lengthSq() - radius * radius)
        if (disc >= 0) globeHit = -bq - Math.sqrt(disc)
      }
      const reach = ((handheld ? 3 : 1.5) * Math.PI) / 180
      let best: number | null = null
      let bestAngle = reach
      const w = scratchD
      for (let i = 0; i < n; i++) {
        if (!engine.mask[i]) continue
        const o = (K * n + i) * 3
        if (p[o] !== p[o]) continue
        w.set(p[o], p[o + 1], p[o + 2]).applyMatrix4(m).sub(origin)
        const dist = w.length()
        if (dist <= 0) continue
        const along = w.dot(dir)
        if (along <= 0) continue
        if (globeHit > 0 && globeHit < along) continue
        const angle = Math.acos(Math.min(1, along / dist))
        if (angle < bestAngle) {
          bestAngle = angle
          best = i
        }
      }
      return best
    },

    dispose() {
      disposeLayout()
      if (orbitLine) orbitLine.geometry.dispose()
      if (markLines) markLines.geometry.dispose()
      markMat.dispose()
      if (fovLines) fovLines.geometry.dispose()
      fovMat.dispose()
      geoRing.geometry.dispose()
      for (const s of labels.values()) {
        s.material.map?.dispose()
        s.material.dispose()
      }
      labels.clear()
      pointMat.dispose()
      trailMat.dispose()
      orbitMat.dispose()
      geoRingMat.dispose()
      engine.releaseReader(reader)
      parent.remove(group)
    },
  }
}

/** "ISS (ZARYA)" → "ISS"; "CSS (TIANHE)" → "Tiangong (CSS)". */
export function shortName(name: string): string {
  if (/^CSS \(TIANHE\)/.test(name)) return 'Tiangong (CSS)'
  const m = /^([^(]+?)\s*\(/.exec(name)
  return (m ? m[1] : name).slice(0, 18)
}
